import { useRef, useLayoutEffect, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fullPagerConfig } from '@/utils/property';
import { Button, Table, Space, Dropdown, Modal, Input, message, Tooltip, Divider } from 'antd';
import AddRoleModal from './AddRoleModal';
import MemberMaintainDrawer from './MemberMaintainDrawer';
import { get, post, put, delte } from '@/core/request';
import './RoleList.less';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY, MAIN_TOOLBAR_DEDUCT_SELECTORS } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';

const { Search } = Input;

export default function RoleList() {
    const navigate = useNavigate();

    const [tableData, setTableData] = useState({});
    const [pageSize, setPageSize] = useState(20);
    const [currPage, setCurrPage] = useState(1);
    const [roleName, setRoleName] = useState('');

    const changePageSize = useCallback(pageSize => {
        setPageSize(pageSize);
    }, []);

    const changeCurrPage = useCallback(page => {
        setCurrPage(page);
    }, []);

    const onSearchName = value => {
        setRoleName(value || undefined);
        setCurrPage(1);
    };

    // fixed-scroll 三件套
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: MAIN_TOOLBAR_DEDUCT_SELECTORS,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const getData = useCallback(() => {
        get(`/enterprise/roles/query_page`, {
            limit: pageSize,
            page: currPage,
            name: roleName,
        }).then(res => {
            if (res.status_code === 200) {
                const { list } = res.data;
                list.forEach(item => {
                    item.key = item.id;
                });
                setTableData(res.data);
            }
        });
    }, [pageSize, currPage, roleName]);

    // 渲染table数据
    useEffect(() => {
        getData();
    }, [getData]);

    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        pageSize,
        size: 'small',
        total: tableData.totalCount,
        onShowSizeChange: (_current, pageSize) => changePageSize(pageSize),
        onChange: page => {
            changeCurrPage(page);
        },
        current: currPage,
    };

    // 点击删除
    const deleteConfirm = row => {
        return () => {
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle"></i>,
                content: '确认是否删除企业',
                okText: '确认',
                cancelText: '取消',
                onOk: () => {
                    if (row.disabled === 0 && row.counts > 0) {
                        message.error('该角色下有成员，不能删除');
                    } else {
                        const form = new FormData();
                        form.append('id', row.id);
                        delte('/enterprise/roles/delete', form).then(res => {
                            if (res.status_code === 200) {
                                message.success(res.message);
                                getData();
                            }
                        });
                    }
                },
            });
        };
    };

    // 启用/禁用账号
    const stopConfirm = row => {
        return () => {
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle"></i>,
                content: '是否要启用/禁用该角色',
                okText: '确认',
                cancelText: '取消',
                onOk: () => {
                    if (row.disabled === 0 && row.counts > 0) {
                        message.error('该角色下有成员，不能禁用');
                    } else {
                        let disabled = 1;
                        if (row.disabled === 1) {
                            disabled = 0;
                        }
                        put('/enterprise/roles/status', {
                            id: row.id,
                            disabled,
                        }).then(res => {
                            if (res.status_code === 200) {
                                message.success(res.message);
                                getData();
                            }
                        });
                    }
                },
            });
        };
    };

    // drawer成员维护
    const drawer = useRef();
    const memberMaintain = useCallback(row => {
        return () => {
            drawer.current.show(row);
        };
    }, []);

    // 新增modal相关
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalData, setModalData] = useState({
        tip: '',
        row: {},
    });

    // 新增弹窗
    const showModal = (tip, row) => {
        return () => {
            setModalData({
                tip,
                row,
            });
            setIsModalVisible(true);
        };
    };
    // 新增角色
    const handleAddRole = useCallback(
        value => {
            post(`/enterprise/roles/save`, value).then(res => {
                if (res.status_code === 200) {
                    message.success(res.message);
                    getData();
                }
            });
        },
        [getData],
    );

    // 编辑角色
    const handleEditRole = useCallback(
        value => {
            put(`/enterprise/roles/update_info`, value).then(res => {
                if (res.status_code === 200) {
                    message.success(res.message);
                    getData();
                }
            });
        },
        [getData],
    );

    const handleRefresh = useCallback(() => {
        message.info('正在刷新', 1);
        changeCurrPage(1);
    }, [changeCurrPage]);

    // 权限维护
    const authorityMaintain = row => {
        return () => {
            navigate(`/enterprise/role/${row.id}/permissions`, { state: row });
        };
    };

    // table 数据相关
    const columns = [
        { title: '角色ID', dataIndex: 'id' },
        { title: '角色名称', dataIndex: 'name' },
        { title: '角色描述', dataIndex: 'description' },
        { title: '成员数', dataIndex: 'counts' },
        {
            title: '状态',
            dataIndex: 'disabled',
            render: operates => <span className={operates === 0 ? 'open-status' : 'close-status'}>{operates === 0 ? '启用' : '禁用'}</span>,
        },
        {
            title: '操作',
            dataIndex: 'operate',
            width: 100,
            fixed: 'right',
            render: (_operates, row) => {
                const items = [
                    {
                        label: '权限维护',
                        key: 'authority',
                        onClick: authorityMaintain(row),
                    },
                    {
                        label: '查看成员',
                        key: 'member',
                        onClick: memberMaintain(row),
                    },
                    {
                        label: row.disabled === 0 ? '禁用角色' : '启用角色',
                        key: 'stop',
                        onClick: stopConfirm(row),
                        ...(row.disabled === 0 ? { danger: true } : {}),
                    },
                ];
                if (row.disabled === 1) {
                    items.push({
                        label: '删除角色',
                        key: 'delete',
                        onClick: deleteConfirm(row),
                        danger: true,
                    });
                }

                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button size="small" type="link" onClick={showModal('edit', row)}>
                            编辑
                        </Button>
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button type="link" size="small" className="more-operation">
                                <i className="bi bi-three-dots" aria-hidden="true" />
                            </Button>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    return (
        <div className="page-container list-page-container enterprise-role-page">
            <PageHeader title="角色管理" breadcrumb={[]} />

            <div ref={tableRegionRef} className="table-region-container enterprise-role-table-region">
                <div className="main-toolbar enterprise-role-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={showModal('add')}>
                                新增角色
                            </Button>
                            <Search placeholder="角色名称关键字" allowClear style={{ width: 250 }} onSearch={onSearchName} />
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>
                <div className="list-table-wrap enterprise-role-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="id"
                        columns={columns}
                        dataSource={tableData.list}
                        loading={false}
                        bordered
                        sticky
                        size="medium"
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                        pagination={paginationProps}
                    />
                </div>
            </div>

            {/* 点击新增的modal */}
            {isModalVisible && (
                <Modal open={isModalVisible} closable={false} footer={null}>
                    <AddRoleModal modalData={modalData} handleAddRole={handleAddRole} setIsModalVisible={setIsModalVisible} handleEditRole={handleEditRole}></AddRoleModal>
                </Modal>
            )}

            <MemberMaintainDrawer ref={drawer} />
        </div>
    );
}
